import type { ErrorEvent } from "@sentry/react";
import { getPostHogSessionReplay } from "@client/telemetry/posthog";
import {
  POSTHOG_REPLAY_URL_HEADER,
  POSTHOG_SESSION_ID_HEADER,
} from "@lib/posthogLinkHeaders";

/**
 * Attach the PostHog session recording, deep-linked to this moment, to a
 * Sentry error event — PostHog records the session; Sentry carries the link.
 *
 * The URL goes in a context (a value too long and too unique to belong in
 * Sentry's indexed tag store); the session id is a tag, which is what tags
 * are for — short, indexed, and searchable, so `posthog_session_id:<id>`
 * pulls up every error from one session. Both surfaces can be promoted into
 * the issue page's Highlights strip by a project admin.
 *
 * Best-effort: PostHog may be uninitialized (no key) or the recording
 * unsampled.
 */
export function attachPostHogSessionReplayUrl(
  event: ErrorEvent,
  getReplay = getPostHogSessionReplay,
): ErrorEvent {
  try {
    const { sessionId, url: sessionReplayUrl } = getReplay();
    if (sessionReplayUrl) {
      event.contexts = {
        ...event.contexts,
        posthog: { session_replay_url: sessionReplayUrl },
      };
      event.tags = {
        ...event.tags,
        posthog_session_id: sessionId,
      };
    }
  } catch {
    // Never let enrichment break error reporting.
  }
  return event;
}

/**
 * Request headers carrying this session's recording identity, so BACKEND
 * errors raised by this API call link to the same recording (see
 * server/lib/posthogRequestLink.ts). Empty when PostHog isn't running — the
 * server simply doesn't tag. The replay URL is minted per request, so its
 * timestamp deep-links near the failing moment.
 */
export function posthogLinkRequestHeaders(
  getReplay = getPostHogSessionReplay,
): Record<string, string> {
  try {
    const { sessionId, url } = getReplay();
    if (!url || !sessionId) return {};
    return {
      [POSTHOG_SESSION_ID_HEADER]: sessionId,
      [POSTHOG_REPLAY_URL_HEADER]: url,
    };
  } catch {
    // Never let enrichment break the request being sent.
    return {};
  }
}
